import { useMemo } from 'react';
import { ForeclosureProperty, CA_COUNTIES_CONFIG } from '../../../mocks/caForeclosures';

interface Filters {
  minTax: number;
  maxTax: number;
  propertyType: string;
  counties: Set<string>;
  status: string;
}

interface MapSidebarProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  filteredProperties: ForeclosureProperty[];
  onCountyFocus: (county: string) => void;
}

const PROPERTY_TYPES = ['All Types', 'Single Family', 'Multi Family', 'Commercial', 'Vacant Land', 'Industrial', 'Condo'];
const STATUS_OPTIONS = ['All Status', 'Active', 'Pending', 'Upcoming', 'Sold'];
const ALL_COUNTIES = Object.keys(CA_COUNTIES_CONFIG);

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export const MapSidebar: React.FC<MapSidebarProps> = ({
  filters,
  onFiltersChange,
  filteredProperties,
  onCountyFocus,
}) => {
  const stats = useMemo(() => {
    const count = filteredProperties.length;
    const totalValue = filteredProperties.reduce((s, p) => s + p.assessedValue, 0);
    const avgTaxes = count > 0 ? filteredProperties.reduce((s, p) => s + p.taxesOwed, 0) / count : 0;
    return { count, totalValue, avgTaxes };
  }, [filteredProperties]);

  const toggleCounty = (county: string) => {
    const next = new Set(filters.counties);
    if (next.has(county)) {
      next.delete(county);
    } else {
      next.add(county);
    }
    onFiltersChange({ ...filters, counties: next });
  };

  const selectAllCounties = () => {
    onFiltersChange({ ...filters, counties: new Set(ALL_COUNTIES) });
  };

  const clearAllCounties = () => {
    onFiltersChange({ ...filters, counties: new Set() });
  };

  const countyCount = (county: string) =>
    filteredProperties.filter(p => p.county === county).length;

  return (
    <aside className="w-80 flex-shrink-0 bg-[#111827] border-r border-gray-700/50 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-700/50">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-filter-3-line text-amber-400 text-base"></i>
          </div>
          <h2 className="text-white font-semibold text-sm tracking-wide uppercase">Map Filters</h2>
        </div>
        <p className="text-gray-400 text-xs">California Tax Foreclosure Properties</p>
      </div>

      {/* Stats */}
      <div className="px-5 py-4 border-b border-gray-700/50 grid grid-cols-3 gap-2">
        <div className="bg-[#1f2937] rounded-lg p-3 text-center">
          <div className="text-amber-400 font-bold text-lg leading-none">{stats.count}</div>
          <div className="text-gray-400 text-xs mt-1">Properties</div>
        </div>
        <div className="bg-[#1f2937] rounded-lg p-3 text-center">
          <div className="text-emerald-400 font-bold text-sm leading-none truncate">{fmt(stats.totalValue)}</div>
          <div className="text-gray-400 text-xs mt-1">Total Value</div>
        </div>
        <div className="bg-[#1f2937] rounded-lg p-3 text-center">
          <div className="text-rose-400 font-bold text-sm leading-none truncate">{fmt(stats.avgTaxes)}</div>
          <div className="text-gray-400 text-xs mt-1">Avg Taxes</div>
        </div>
      </div>

      {/* Scrollable filter area */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5 scrollbar-thin">

        {/* Price Range Slider */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-3">
            Taxes Owed Range
          </label>
          <div className="space-y-3">
            <div className="flex justify-between text-xs text-gray-400">
              <span>{fmt(filters.minTax)}</span>
              <span>{fmt(filters.maxTax)}</span>
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Min: {fmt(filters.minTax)}</label>
                <input
                  type="range"
                  min={0}
                  max={50000}
                  step={500}
                  value={filters.minTax}
                  onChange={e => {
                    const v = Number(e.target.value);
                    if (v < filters.maxTax) onFiltersChange({ ...filters, minTax: v });
                  }}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: '#f59e0b' }}
                />
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-1 block">Max: {fmt(filters.maxTax)}</label>
                <input
                  type="range"
                  min={0}
                  max={50000}
                  step={500}
                  value={filters.maxTax}
                  onChange={e => {
                    const v = Number(e.target.value);
                    if (v > filters.minTax) onFiltersChange({ ...filters, maxTax: v });
                  }}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: '#f59e0b' }}
                />
              </div>
            </div>
            <div className="flex gap-2">
              {[10000, 25000, 50000].map(preset => (
                <button
                  key={preset}
                  onClick={() => onFiltersChange({ ...filters, minTax: 0, maxTax: preset })}
                  className={`flex-1 text-xs py-1 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                    filters.maxTax === preset && filters.minTax === 0
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : 'border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}
                >
                  &lt;{fmt(preset / 1000)}k
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Property Type */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">
            Property Type
          </label>
          <select
            value={filters.propertyType}
            onChange={e => onFiltersChange({ ...filters, propertyType: e.target.value })}
            className="w-full bg-[#1f2937] border border-gray-600 text-gray-200 text-sm rounded-lg px-3 py-2.5 cursor-pointer focus:outline-none focus:border-amber-500/60"
          >
            {PROPERTY_TYPES.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        {/* Status */}
        <div>
          <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wide mb-2">
            Auction Status
          </label>
          <div className="grid grid-cols-2 gap-1.5">
            {STATUS_OPTIONS.map(s => (
              <button
                key={s}
                onClick={() => onFiltersChange({ ...filters, status: s })}
                className={`text-xs py-1.5 px-2 rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                  filters.status === s
                    ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                    : 'border-gray-600 text-gray-400 hover:border-gray-500'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Counties */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-gray-300 text-xs font-semibold uppercase tracking-wide">
              Counties ({filters.counties.size}/{ALL_COUNTIES.length})
            </label>
            <div className="flex gap-2">
              <button
                onClick={selectAllCounties}
                className="text-xs text-amber-400 hover:text-amber-300 cursor-pointer whitespace-nowrap"
              >
                All
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={clearAllCounties}
                className="text-xs text-gray-400 hover:text-gray-300 cursor-pointer whitespace-nowrap"
              >
                None
              </button>
            </div>
          </div>
          <div className="space-y-1">
            {ALL_COUNTIES.map(county => {
              const config = CA_COUNTIES_CONFIG[county];
              const count = countyCount(county);
              const checked = filters.counties.has(county);
              return (
                <div
                  key={county}
                  className="flex items-center gap-2.5 py-2 px-2.5 rounded-lg hover:bg-[#1f2937] transition-colors group"
                >
                  <button
                    onClick={() => toggleCounty(county)}
                    className={`w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-all cursor-pointer`}
                    style={{
                      borderColor: config.color,
                      backgroundColor: checked ? config.color : 'transparent',
                    }}
                  >
                    {checked && <i className="ri-check-line text-white text-xs leading-none"></i>}
                  </button>
                  <button
                    onClick={() => { if (checked) onCountyFocus(county); }}
                    className="flex-1 flex items-center justify-between text-left cursor-pointer"
                  >
                    <span className={`text-sm transition-colors ${checked ? 'text-gray-200' : 'text-gray-500'}`}>
                      {county}
                    </span>
                    <span
                      className="text-xs font-semibold px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: checked ? `${config.color}22` : '#1f293700',
                        color: checked ? config.color : '#6b7280',
                      }}
                    >
                      {count}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Reset */}
        <button
          onClick={() => onFiltersChange({
            minTax: 0,
            maxTax: 50000,
            propertyType: 'All Types',
            counties: new Set(ALL_COUNTIES),
            status: 'All Status',
          })}
          className="w-full py-2.5 border border-gray-600 text-gray-400 text-sm rounded-lg hover:border-amber-500/50 hover:text-amber-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line mr-2"></i>Reset All Filters
        </button>
      </div>
    </aside>
  );
};

export default MapSidebar;
